import { Component, type OnInit, signal, computed, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash';
import { AuthService } from '@card-software/nexus-utilities';
import { BreadcrumbService } from '@card-software/library';
import { ProcessProxyService } from '../../../proxies/item/process.proxy.service';
import { ItemMasterProxyService } from '../../../proxies/item/item-master.proxy.service';
import { StepProxyService } from '../../../proxies/item/step.proxy.service';
import { LocaleHelperService } from '../../../services/localehelperservice.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { Process, ProcessBase, StepIdsWithOrder } from '../../../models/engineering/process';
import { InputParameter } from '../../../models/engineering/input-parameter';
import { ItemDetailsFormGroup, ItemConsumableDetails } from '../../../models/engineering/item-consumable-details';
import { ProcessType } from '../../../models/engineering/process-type';
import { ItemMaster } from '../../../models/item-master';
import { Step } from '../../../models/engineering/step';
import { ProductDevelopmentAdminGroup, ProductDevelopmentViewerGroup } from '../../../models/authorization/enum/user-group.enum';

@Component({
  selector: 'app-process-details',
  templateUrl: './process-details.component.html',
  styleUrl: './process-details.component.scss',
})
export class ProcessDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly processProxyService = inject(ProcessProxyService);
  private readonly itemMasterProxyService = inject(ItemMasterProxyService);
  private readonly stepProxyService = inject(StepProxyService);
  private readonly translateService = inject(TranslateService);
  private readonly localeHelper = inject(LocaleHelperService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  readonly process = signal<Process | null>(null);
  readonly isLoading = signal<number>(0);
  readonly allProcessTypes = signal<ProcessType[]>([]);
  readonly inventoryTypeReference = signal<string>('6866c33a245e3504d98d3a44');
  readonly itemDetailsFormGroups = new FormArray<FormGroup<ItemDetailsFormGroup>>([]);

  readonly isEditing = computed(() => !_.isNil(this.process()));

  readonly canEditItems = signal<boolean>(false);
  readonly canEditParameters = signal<boolean>(false);

  readonly canEditPermission = computed(() => {
    if (AuthService.hasRole(ProductDevelopmentAdminGroup.ProcessesAdmin)) {
      return true;
    }
    if (AuthService.hasRole(ProductDevelopmentViewerGroup.ProcessesViewer)) {
      return false;
    }
    return false;
  });

  readonly processType = new FormControl<string | null>(null);
  readonly name = new FormControl<string>('', [Validators.required]);
  readonly description = new FormControl<string>('', [Validators.required]);
  readonly showInFabricGeneralProcessRoute = new FormControl<boolean>(false, { nonNullable: true });
  readonly steps = new FormControl<Step[]>([], { nonNullable: true });
  readonly additionalParameters = new FormControl<InputParameter[]>([], { nonNullable: true });

  readonly formGroup = new FormGroup({
    processType: this.processType,
    name: this.name,
    description: this.description,
    showInFabricGeneralProcessRoute: this.showInFabricGeneralProcessRoute,
    steps: this.steps,
    additionalParameters: this.additionalParameters,
  });

  canEdit(): boolean {
    return this.canEditPermission();
  }

  ngOnInit(): void {
    this.loadProcessFromRoute();
    this.setupFormPermissions();
  }

  private incrementLoading(): void {
    this.isLoading.update((current) => current + 1);
  }

  private decrementLoading(): void {
    this.isLoading.update((current) => Math.max(0, current - 1));
  }

  private loadProcessTypes() {
    return this.processProxyService.getProcessTypes();
  }

  private loadProcessFromRoute(): void {
    this.route.queryParams.subscribe((params) => {
      const processId = params['id'];

      if (!processId) {
        this.router.navigate(['/processes']);
        return;
      }

      if (processId === 'new') {
        this.breadcrumbService.setCurrent('Common_New');
        this.incrementLoading();
        this.loadProcessTypes()
          .pipe(finalize(() => this.decrementLoading()))
          .subscribe({
            next: (processTypes) => {
              this.allProcessTypes.set(processTypes.data || []);
            },
          });
        return;
      }

      this.incrementLoading();
      forkJoin({
        processTypes: this.loadProcessTypes(),
        process: this.processProxyService.getProcessById(processId),
      })
        .pipe(finalize(() => this.decrementLoading()))
        .subscribe({
          next: (results) => {
            this.allProcessTypes.set(results.processTypes.data || []);

            if (results.process === null) {
              this.router.navigate(['/processes']);
            } else {
              this.process.set(results.process.data);
              this.breadcrumbService.setCurrent(results.process.data?.processId?.toString());
              this.populateForm();
            }
          },
        });
    });
  }

  private setupFormPermissions(): void {
    if (!this.canEdit()) {
      this.formGroup.disable();
    }
  }

  private populateForm(): void {
    const currentProcess = this.process();

    if (_.isNil(currentProcess)) {
      console.error('Process is null or undefined');
      return;
    }

    this.processType.setValue(this.allProcessTypes().find((pt) => pt.id === currentProcess.processTypeReference)?.id || null);
    this.name.setValue(currentProcess.name.en || '');
    this.description.setValue(currentProcess.description.en || '');
    this.showInFabricGeneralProcessRoute.setValue(currentProcess.showInFabricGeneralProcessRoute || false);

    // Cargar steps completos desde stepIdsWithOrder
    if (currentProcess.stepIdsWithOrder && currentProcess.stepIdsWithOrder.length > 0) {
      this.loadStepsFromIds(currentProcess.stepIdsWithOrder);
    }

    if (currentProcess.additionalParameters && currentProcess.additionalParameters.length > 0) {
      this.additionalParameters.setValue(currentProcess.additionalParameters);
    }

    if (currentProcess.itemDetails && currentProcess.itemDetails.length > 0) {
      this.loadItemDetailsFromProcess(currentProcess.itemDetails);
    }
  }

  private loadItemDetailsFromProcess(itemDetails: ItemConsumableDetails[]): void {
    const itemCodes = itemDetails.map((item) => item.itemCode);

    this.itemMasterProxyService.getItemCodesBatch({ itemCodes }).subscribe({
      next: (response) => {
        if (response.data) {
          const itemsWithLocalized = response.data.map((item: ItemMaster) => ({
            ...item,
            itemDisplayNameLocalized: this.localeHelper.getLocalizedValue(item.itemDisplayName),
          }));

          this.itemDetailsFormGroups.clear();
          itemDetails.forEach((itemDetail) => {
            const item = itemsWithLocalized.find((i) => i.itemCode === itemDetail.itemCode);
            if (item) {
              this.itemDetailsFormGroups.push(this.createItemDetailFormGroup(itemDetail, item));
            }
          });
        }
      },
    });
  }

  private createItemDetailFormGroup(itemDetail: ItemConsumableDetails, item: ItemMaster): FormGroup<ItemDetailsFormGroup> {
    return new FormGroup<ItemDetailsFormGroup>({
      item: new FormControl<ItemMaster>(item, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      isPreferred: new FormControl<boolean>(itemDetail.isPreferred ?? false, {
        nonNullable: true,
      }),
      ingredientUnit: new FormControl(itemDetail.ingredientUnit ?? null),
      scalingMethod: new FormControl(itemDetail.scalingMethod ?? null),
      quantity: new FormControl(itemDetail.quantity ?? 0),
    });
  }

  private getProcessToUpdate(processBase: ProcessBase): Process {
    const currentProcess = this.process();
    if (!currentProcess) {
      throw new Error('Cannot update process: current process is null');
    }

    return {
      ...processBase,
      processId: currentProcess.processId,
      id: currentProcess.id,
    };
  }

  onSubmit(): void {
    if (!this.isFormValidForSubmission()) {
      return;
    }

    const processBase = this.buildProcessBaseFromForm();
    const operation = this.isEditing()
      ? this.processProxyService.updateProcess(this.getProcessToUpdate(processBase))
      : this.processProxyService.insertProcess(processBase);

    this.executeSaveOperation(operation, processBase);
  }

  private isFormValidForSubmission(): boolean {
    if (this.formGroup.invalid) {
      GlobalMessageUtil.warn('Missing required fields', 'Process Details');
      return false;
    }

    const formValues = this.formGroup.value;

    if (
      _.isNil(formValues.processType) ||
      _.isNil(formValues.name) ||
      _.isNil(formValues.description) ||
      _.isNil(formValues.showInFabricGeneralProcessRoute)
    ) {
      return false;
    }

    return true;
  }

  private buildProcessBaseFromForm(): ProcessBase {
    const formValues = this.formGroup.value;

    return {
      processTypeReference: formValues.processType!,
      name: { en: formValues.name!, es: '' },
      description: { en: formValues.description!, es: '' },
      showInFabricGeneralProcessRoute: formValues.showInFabricGeneralProcessRoute!,
      itemDetails: this.extractItemDetailsFromForm(),
      additionalParameters: this.additionalParameters.value,
      stepIdsWithOrder: this.convertStepsToStepIdsWithOrder(formValues.steps || []),
      notes: [],
      assetTypeReferences: [],
    };
  }

  private convertStepsToStepIdsWithOrder(steps: Step[]): StepIdsWithOrder[] {
    return steps.map((step, index) => ({
      stepId: step.stepId,
      order: index + 1,
    }));
  }

  private loadStepsFromIds(stepIdsWithOrder: StepIdsWithOrder[]): void {
    const uniqueStepIds = _.uniq(stepIdsWithOrder.map((s) => s.stepId));

    if (uniqueStepIds.length === 0) {
      this.steps.setValue([]);
      return;
    }

    this.incrementLoading();

    const stepRequests = uniqueStepIds.map((stepId) => this.stepProxyService.getStepById(stepId.toString()));

    forkJoin(stepRequests)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (responses) => {
          const stepsMap = new Map<number, Step>();
          responses.forEach((response) => {
            if (response.data) {
              stepsMap.set(response.data.stepId, response.data);
            }
          });

          // Ordenar steps según stepIdsWithOrder
          const orderedSteps = stepIdsWithOrder.map((swo) => stepsMap.get(swo.stepId)).filter((step): step is Step => !_.isNil(step));

          this.steps.setValue(orderedSteps);
        },
        error: () => {
          this.steps.setValue([]);
        },
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private extractItemDetailsFromForm(): any[] {
    return this.itemDetailsFormGroups.controls.map((formGroup) => ({
      itemCode: formGroup.value.item?.itemCode ?? '',
      isPreferred: formGroup.value.isPreferred ?? false,
      ingredientUnit: formGroup.value.ingredientUnit ?? null,
      scalingMethod: formGroup.value.scalingMethod ?? null,
      quantity: formGroup.value.quantity ?? 0,
    }));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private executeSaveOperation(operation: any, processBase: ProcessBase): void {
    this.incrementLoading();

    operation.pipe(finalize(() => this.decrementLoading())).subscribe({
      next: (response: { data?: Process }) => {
        const responseProcess = response.data ?? this.getProcessToUpdate(processBase);
        this.showSuccessMessageForProcess(responseProcess);
        setTimeout(() => {
          this.router.navigate(['/processes']);
        }, 1500);
      },
    });
  }

  private showSuccessMessageForProcess(process: Process): void {
    const message = this.isEditing()
      ? this.translateService.instant('Process_Updated', { name: process.processId })
      : this.translateService.instant('Process_Created', { name: process.processId });

    GlobalMessageUtil.success('Success', message);
  }

  cancel(): void {
    this.router.navigate(['/processes']);
  }
}
